import React from "react";
import { MapPinIcon } from "@heroicons/react/20/solid";
import ScopeWithAdditionalTags from "../ScopeWithAdditionalTags";
import CalendarRowBadgeStack from "./CalendarRowBadgeStack";
import CalendarVendorSponsorLine from "./CalendarVendorSponsorLine";
import CalendarAssessmentActionMenu from "./CalendarAssessmentActionMenu";
import { addedScopeLabels } from "../../utils/assessmentCalendarRules";
import {
  formatCalendarLocation,
  formatOnsiteRemote,
  formatRequestScheduleLine,
  mapCapabilitiesForScope,
  providerInitial,
  resolveProviderDisplayName,
} from "../../utils/calendarRowDisplay";

const CalendarAssessmentRow = ({
  row,
  isAdmin,
  today,
  onOpen,
  onRequestToJoin,
  onRefetch,
  isSubrow = false,
  compact = false,
}) => {
  const added = isSubrow ? addedScopeLabels(row) : [];
  const logoSize = compact ? "h-10 w-10 text-sm" : "h-14 w-14 text-xl";
  const padding = compact ? "py-3" : "py-4";
  const showLogo = !isSubrow;

  return (
    <div
      className={`group relative flex gap-4 rounded-lg px-2 ${padding} transition-colors hover:bg-gray-50`}
    >
      <button
        type="button"
        aria-label={`Open ${resolveProviderDisplayName(row)} assessment`}
        className="absolute inset-0 z-0 cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        onClick={() => onOpen(row)}
      />
      {showLogo ? (
        <div
          className={`pointer-events-none relative flex flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 font-bold text-indigo-700 ${logoSize}`}
          aria-hidden="true"
        >
          {providerInitial(resolveProviderDisplayName(row))}
        </div>
      ) : null}
      <div className="relative z-10 min-w-0 flex-auto text-left">
        <div className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1">
          {/* Empty space still opens the row; vendor/sponsor name links stay clickable. */}
          <div className="pointer-events-none min-w-0 flex-1 overflow-hidden [&_button]:pointer-events-auto">
            <CalendarVendorSponsorLine row={row} isSubrow={isSubrow} compact={compact} />
          </div>
          <div className="relative z-10 ml-auto flex flex-shrink-0 items-start gap-2">
            <CalendarRowBadgeStack row={row} isAdmin={isAdmin} today={today} className="ml-0" />
            <CalendarAssessmentActionMenu
              row={row}
              isAdmin={isAdmin}
              onOpen={onOpen}
              onRequestToJoin={onRequestToJoin}
              refetch={onRefetch}
            />
          </div>
        </div>
        <p className="pointer-events-none mt-2 text-xs text-gray-500">
          {formatRequestScheduleLine(row, { showAuditor: isAdmin })}
        </p>
        <div className="pointer-events-none mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          <span>{formatOnsiteRemote(row.vqa_type)}</span>
          <span aria-hidden="true">·</span>
          <span className="inline-flex min-w-0 items-center gap-1">
            <MapPinIcon className="h-3 w-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
            <span className="min-w-0 break-words">{formatCalendarLocation(row)}</span>
          </span>
        </div>
        {!isSubrow ? (
          <div className="relative z-10 mt-2">
            <ScopeWithAdditionalTags
              services={mapCapabilitiesForScope(row)}
              additionalScope={row.additional_scope}
              showAdditionalScope
            />
          </div>
        ) : null}
        {added.length > 0 ? (
          <div className="relative z-10 mt-2">
            <ScopeWithAdditionalTags
              services={[]}
              showAdditionalScope={false}
              addedScope={(row.added_scope || []).map((item) => item?.rfi_name).filter(Boolean)}
              addedScopeTags={[row.addon_scope_tags, row.sponsor_additional_scope_tags]
                .filter(Boolean)
                .join(", ")}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CalendarAssessmentRow;
